import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, MapPin, Users, Landmark, UserCheck, Building2, Info, CalendarDays, Clock, GitBranch, FileText, ClipboardList, Euro, Receipt, Workflow, StickyNote, Sparkles, Pencil } from 'lucide-react';

const STATUT = { en_cours: ['En cours', 'bg-cyan-100 text-cyan-700'], planifie: ['Planifiée', 'bg-amber-100 text-amber-700'], termine: ['Terminée', 'bg-emerald-100 text-emerald-700'], annule: ['Annulée', 'bg-red-100 text-red-700'] };
const fmt = (d) => (d ? format(parseISO(d), 'd MMM yyyy', { locale: fr }) : '—');

export const SECTIONS = [
  { key: 'general', label: 'Informations générales', icon: Info },
  { key: 'planning', label: 'Planning', icon: CalendarDays },
  { key: 'planning_pedagogique', label: 'Planning pédagogique', icon: Clock },
  { key: 'fil_chronologique', label: 'Fil chronologique', icon: GitBranch },
  { key: 'rapports', label: 'Rapports', icon: FileText },
  { key: 'bilan', label: 'Bilans', icon: ClipboardList },
  { key: 'financement', label: 'Dossier financeur', icon: Euro },
  { key: 'facturation', label: 'Facturation', icon: Receipt },
  { key: 'workflow', label: 'Workflow', icon: Workflow },
  { key: 'notes', label: 'Notes & observations', icon: StickyNote, formateur: true },
  { key: 'synthese', label: 'Synthèse du groupe', icon: Sparkles, formateur: true },
];

// Tuile principale de l'aperçu d'une action (style portail financeur) + boutons d'accès aux sections
export const ApercuActionCard = ({ action, apercu, dossierId }) => {
  const navigate = useNavigate();
  const [stLabel, stCls] = STATUT[action.statut] || [action.statut, 'bg-slate-100 text-slate-600'];
  const pastDates = (action.planning || []).map(p => p.date).filter(d => d <= new Date().toISOString().slice(0, 10));
  const journeeDate = pastDates[pastDates.length - 1] || (action.planning || [])[0]?.date;
  const go = (s) => s.formateur
    ? navigate(journeeDate ? `/portail-formateur?dossier=${dossierId}&date=${journeeDate}` : '/portail-formateur')
    : navigate(`/dossiers/${dossierId}/edit?tab=${s.key}&from=apercu`);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5" data-testid="apercu-action-card">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-slate-500">{action.code}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stCls}`} data-testid="apercu-statut">{stLabel}</span>
            {apercu.type_dossier === 'aft' && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-cyan-600 text-white">Action de certification</span>}
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="apercu-intitule">{action.intitule}</h2>
            <p className="text-sm text-slate-600">{action.formation_code} — {action.formation_titre}</p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-slate-400" /> {fmt(action.date_debut)} → {fmt(action.date_fin)}</span>
            {action.lieu_principal && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {action.lieu_principal}</span>}
            {action.formateurs?.length > 0 && <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-slate-400" /> {action.formateurs.join(', ')}</span>}
            {apercu.referent && <span className="flex items-center gap-1.5"><UserCheck className="w-4 h-4 text-slate-400" /> Référent : {apercu.referent}</span>}
            {apercu.client && <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-slate-400" /> {apercu.client}</span>}
          </div>
          <div data-testid="apercu-financeurs">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1 mb-1"><Landmark className="w-3.5 h-3.5" /> Financeurs</p>
            {apercu.financeurs.length === 0 ? <p className="text-xs text-slate-400">Aucun dossier financeur saisi</p> : (
              <div className="flex flex-wrap gap-1.5">
                {apercu.financeurs.map(f => (
                  <button key={f.id} type="button" onClick={() => navigate(`/dossiers/${dossierId}/edit?tab=financement&from=apercu`)} className="text-xs px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100" data-testid={`apercu-financeur-${f.id}`}>
                    <span className="font-mono text-[10px] text-amber-600 mr-1">{f.code}</span><span className="font-semibold">{f.financeur_nom || '—'}</span>{f.entreprise_finale ? ` · ${f.entreprise_finale}` : ''} · {f.nb_candidats} cand.
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="lg:w-[340px] shrink-0 space-y-3">
          <div className="grid grid-cols-2 gap-1.5" data-testid="apercu-sections">
            {SECTIONS.map(s => (
              <button key={s.key} type="button" onClick={() => go(s)} className="flex items-center gap-1.5 px-2.5 py-2 rounded-md border text-xs text-left text-slate-700 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-colors" data-testid={`apercu-section-${s.key}`}>
                <s.icon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{s.label}</span>
              </button>
            ))}
            <button type="button" onClick={() => navigate(`/dossiers/${dossierId}/edit?from=apercu`)} className="col-span-2 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-md text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700" data-testid="apercu-modifier">
              <Pencil className="w-3.5 h-3.5" /> Modifier l'action
            </button>
          </div>
          <div>
            <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Avancement</span><span className="font-semibold text-slate-700" data-testid="apercu-avancement">{action.jours_passes}/{action.total_jours} j · {action.progression}%</span></div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-cyan-500 rounded-full" style={{ width: `${action.progression}%` }} /></div>
          </div>
        </div>
      </div>
    </div>
  );
};
